// cart-upsells — fetches recommendations from Rebuy's storefront API and
// renders cards in our own markup. We control layout, styling, and refresh
// timing instead of waiting for Rebuy's app embed to hydrate <div data-rebuy-id>.
//
// Primary fetch: Rebuy Widget Recommendations REST API
//   GET https://rebuyengine.com/api/v1/widgets/<id>/products?shop=...&cart=...
// Fallback: window.Rebuy SDK if it loaded.
//
// Depends on: pubsub.js (subscribe, publish), constants.js (PUB_SUB_EVENTS)

(function () {
  'use strict';

  var REBUY_RECOMMENDED_URL = 'https://rebuyengine.com/api/v1/products/recommended';
  var FETCH_DEBOUNCE_MS = 250;
  var SKELETON_COUNT = 3;
  var MAX_CARDS = 12;

  function shopDomain() {
    return (window.Shopify && window.Shopify.shop) || '';
  }
  function activeCurrency() {
    return (
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      'USD'
    );
  }
  function formatPrice(value) {
    // Rebuy's recommended-products endpoint returns prices as decimal strings
    // ("8.00", "12.50"). Some other Rebuy endpoints return integer cents (800).
    // Detect dollars by the presence of a decimal point in the source string;
    // otherwise treat 4+ digit integers as cents (a single Shopify cents value
    // for a $50 product is 5000 — anything < 100 is almost certainly already
    // dollars).
    if (value == null || value === '') return '';
    var str = String(value);
    var hasDecimal = str.indexOf('.') !== -1;
    var n = parseFloat(str);
    if (!isFinite(n)) return '';
    var dollars = hasDecimal ? n : (Number.isInteger(n) && n >= 1000 ? n / 100 : n);
    // Show whole dollars cleanly ($8) but always render two decimals when cents exist ($237.60).
    var hasCents = Math.round(dollars * 100) % 100 !== 0;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: activeCurrency(),
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: 2,
      }).format(dollars);
    } catch (e) {
      return '$' + dollars.toFixed(hasCents ? 2 : 0);
    }
  }
  function shopifyImageAt(url, width) {
    if (!url) return '';
    return url.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, '_' + width + 'x.$1$2');
  }
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c];
    });
  }
  function escapeAttr(s) {
    return String(s == null ? '' : s).replace(/"/g, '&quot;');
  }

  // Normalize whatever Rebuy returns. Keep the full variants array so cards
  // can render an option picker when there's more than one selectable variant.
  function normalizeProduct(p) {
    if (!p) return null;
    var variants = Array.isArray(p.variants) ? p.variants : [];
    var firstVariant = variants[0] || {};
    var image =
      p.featured_image ||
      p.image ||
      (p.images && (p.images[0].src || p.images[0])) ||
      '';
    var url = p.url || (p.handle ? '/products/' + p.handle : '#');
    var optionName = '';
    if (Array.isArray(p.options) && p.options[0]) {
      optionName = p.options[0].name || (typeof p.options[0] === 'string' ? p.options[0] : '');
    }
    return {
      id: p.id || p.product_id,
      title: p.title || p.name || '',
      url: url,
      image: typeof image === 'string' ? image : image.src || '',
      variantId: firstVariant.id || p.variant_id || p.variants_id,
      price: firstVariant.price != null ? firstVariant.price : p.price,
      comparePrice:
        firstVariant.compare_at_price != null
          ? firstVariant.compare_at_price
          : p.compare_at_price,
      variants: variants,
      optionName: optionName || 'option',
      handle: p.handle,
    };
  }

  // ---------------------------------------------------------------------------
  // <cart-upsells> custom element
  // ---------------------------------------------------------------------------
  class CartUpsells extends HTMLElement {
    constructor() {
      super();
      this._unsub = null;
      this._abortCtrl = null;
      this._refreshTimer = null;
      this._cartToken = null;
    }

    connectedCallback() {
      this.widgetId = this.dataset.widgetId || this.getAttribute('widget-id');
      this.apiKey = this.dataset.apiKey || this.getAttribute('api-key') || '';
      this.context = this.dataset.context || 'drawer';
      this.title = this.dataset.title || 'You might also like';

      if (!this.widgetId) {
        this.innerHTML = '';
        return;
      }

      this._renderSkeleton();

      // Subscribe to cart updates (debounced). Skip attribute-only mutations
      // (/cart/update.js) — those don't change the line items, so the
      // recommendations are still valid and there's no need to refetch.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          var url = (event && event.url) || '';
          if (/\/cart\/update(\.js)?$/.test(url)) return;
          this._scheduleRefresh();
        });
      }

      this._refresh();
    }

    disconnectedCallback() {
      if (this._unsub) this._unsub();
      if (this._abortCtrl) this._abortCtrl.abort();
      if (this._refreshTimer) clearTimeout(this._refreshTimer);
      if (this._scrollPoll) clearInterval(this._scrollPoll);
    }

    _scheduleRefresh() {
      if (this._refreshTimer) clearTimeout(this._refreshTimer);
      this._refreshTimer = setTimeout(() => this._refresh(), FETCH_DEBOUNCE_MS);
    }

    async _refresh() {
      if (this._abortCtrl) this._abortCtrl.abort();
      this._abortCtrl = new AbortController();
      var signal = this._abortCtrl.signal;

      try {
        var productIds = await this._currentCartProductIds(signal);
        var products = await this._fetchRebuy(signal, productIds);
        if (!products || !products.length) {
          if (!this.querySelector('.cart-upsells__card:not(.cart-upsells__card--skeleton)')) {
            this.innerHTML = '';
          }
          return;
        }
        var normalized = products.map(normalizeProduct).filter(Boolean).slice(0, MAX_CARDS);
        // _renderCards sets this.innerHTML internally and binds listeners to
        // those elements. Calling it for its side effect — don't re-assign
        // innerHTML from its return value, that would replace the bound DOM.
        this._renderCards(normalized);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.warn('[cart-upsells] refresh failed; keeping previous render', err);
        }
      }
    }

    async _currentCartProductIds(signal) {
      try {
        var cart = await fetch('/cart.js', { signal }).then((r) => r.json());
        if (!cart || !Array.isArray(cart.items)) return [];
        return cart.items.map(function (i) { return i.product_id; }).filter(Boolean);
      } catch (e) {
        return [];
      }
    }

    async _fetchRebuy(signal, productIds) {
      // Rebuy's recommended-products endpoint:
      //   GET /api/v1/products/recommended?key=&shopify_product_ids=&limit=&filter_oos=yes
      // (https://developers.rebuyengine.com/reference/recommended/)
      var url = new URL(REBUY_RECOMMENDED_URL);
      if (this.apiKey) url.searchParams.set('key', this.apiKey);
      if (productIds && productIds.length) {
        url.searchParams.set('shopify_product_ids', productIds.join(','));
      }
      url.searchParams.set('limit', String(MAX_CARDS));
      url.searchParams.set('filter_oos', 'yes');
      var requestUrl = url.toString();
      var r = await fetch(requestUrl, { signal, credentials: 'omit' });
      if (!r.ok) {
        var body = await r.text().catch(function () { return '(no body)'; });
        console.warn('[cart-upsells] Rebuy', r.status, requestUrl, '→', body);
        throw new Error('REST ' + r.status);
      }
      var data = await r.json();
      return data.data || data.products || (Array.isArray(data) ? data : []);
    }

    _arrowsHtml() {
      return (
        '<button type="button" class="cart-upsells__arrow cart-upsells__arrow--prev" data-arrow="prev" aria-label="Previous">' +
          '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M9 2L4 7l5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</button>' +
        '<button type="button" class="cart-upsells__arrow cart-upsells__arrow--next" data-arrow="next" aria-label="Next">' +
          '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M5 2l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</button>'
      );
    }

    _renderSkeleton() {
      var skeletons = '';
      for (var i = 0; i < SKELETON_COUNT; i++) {
        skeletons += '<article class="cart-upsells__card cart-upsells__card--skeleton" aria-hidden="true"></article>';
      }
      this.innerHTML = this._wrapperHtml(skeletons);
      this._wireScrollerInputs();
    }

    _renderCards(products) {
      var cards = products.map((p) => this._renderCard(p)).join('');
      this.innerHTML = this._wrapperHtml(cards);
      this.querySelectorAll('[data-variant-select]').forEach(function (sel) {
        sel.setCustomValidity('');
      });
      this._wireScrollerInputs();
    }

    _wrapperHtml(slidesHtml) {
      var labelId = 'cart-upsells-title-' + (this.widgetId || 'x');
      return (
        '<div class="cart-upsells">' +
          '<h3 class="cart-upsells__title" id="' + labelId + '">' + escapeHtml(this.title) + '</h3>' +
          '<div class="cart-upsells__scroller"' +
            ' role="region"' +
            ' aria-labelledby="' + labelId + '"' +
            ' tabindex="0">' +
            slidesHtml +
          '</div>' +
          this._arrowsHtml() +
          '<div class="cart-upsells__bar"' +
            ' role="scrollbar"' +
            ' aria-orientation="horizontal"' +
            ' aria-valuemin="0"' +
            ' aria-valuemax="100"' +
            ' aria-valuenow="0"' +
            ' aria-controls="' + labelId + '">' +
            '<div class="cart-upsells__bar-thumb"></div>' +
          '</div>' +
        '</div>'
      );
    }

    _wireScrollerInputs() {
      if (this._scrollPoll) clearInterval(this._scrollPoll);

      var scroller = this.querySelector('.cart-upsells__scroller');
      var thumb = this.querySelector('.cart-upsells__bar-thumb');
      var bar = this.querySelector('[role="scrollbar"]');
      if (!scroller) return;
      var self = this;

      var prevArrow = this.querySelector('[data-arrow="prev"]');
      var nextArrow = this.querySelector('[data-arrow="next"]');

      var updateThumb = function () {
        var sw = scroller.scrollWidth;
        var cw = scroller.clientWidth;
        var max = sw - cw;
        var progressPct = 0;
        if (thumb) {
          if (sw <= cw + 1) {
            thumb.style.width = '100%';
            thumb.style.left = '0%';
          } else {
            var visible = cw / sw;
            var progress = scroller.scrollLeft / max;
            progressPct = Math.round(progress * 100);
            thumb.style.width = (visible * 100) + '%';
            thumb.style.left = (progress * (1 - visible) * 100) + '%';
          }
        }
        if (bar) bar.setAttribute('aria-valuenow', String(progressPct));
        // Hide arrows at the edges so the user can't click into a no-op.
        if (prevArrow) {
          prevArrow.classList.toggle('is-disabled', scroller.scrollLeft <= 1);
          prevArrow.setAttribute('aria-disabled', String(scroller.scrollLeft <= 1));
        }
        if (nextArrow) {
          nextArrow.classList.toggle('is-disabled', scroller.scrollLeft >= max - 1);
          nextArrow.setAttribute('aria-disabled', String(scroller.scrollLeft >= max - 1));
        }
      };

      // Click+drag the bar to scroll — smooth delta-based scrubbing matching
      // the card-drag feel. We DON'T jump-on-click; instead we track the start
      // cursor position and translate cursor delta to scroll delta with the
      // bar→scroller ratio. No discontinuity, 1:1 thumb-follows-cursor.
      if (bar) {
        bar.addEventListener('pointerdown', function (evt) {
          evt.preventDefault();
          evt.stopPropagation();
          bar.setPointerCapture && bar.setPointerCapture(evt.pointerId);
          bar.classList.add('is-dragging');

          var rect = bar.getBoundingClientRect();
          var sw = scroller.scrollWidth;
          var cw = scroller.clientWidth;
          var max = sw - cw;
          var visible = cw / sw;
          // px of bar travel ÷ px of scroll movement.
          // thumb position on bar = (scrollLeft / max) × (1 − visible) × barWidth
          // → 1 px of cursor ≈ max / ((1 − visible) × barWidth) px of scroll.
          var ratio = max <= 0 || rect.width <= 0
            ? 0
            : max / (Math.max(0.0001, 1 - visible) * rect.width);

          var startX = evt.clientX;
          var startScroll = scroller.scrollLeft;

          var onMove = function (em) {
            var dx = em.clientX - startX;
            scroller.scrollLeft = Math.max(0, Math.min(max, startScroll + dx * ratio));
          };
          var onUp = function () {
            bar.classList.remove('is-dragging');
            bar.removeEventListener('pointermove', onMove);
            bar.removeEventListener('pointerup', onUp);
            bar.removeEventListener('pointercancel', onUp);
          };
          bar.addEventListener('pointermove', onMove);
          bar.addEventListener('pointerup', onUp);
          bar.addEventListener('pointercancel', onUp);
        });
      }

      // Keyboard arrow-key panning when the scroller has focus.
      scroller.addEventListener('keydown', function (evt) {
        if (evt.key === 'ArrowRight') {
          evt.preventDefault();
          scrollByCard(1);
        } else if (evt.key === 'ArrowLeft') {
          evt.preventDefault();
          scrollByCard(-1);
        } else if (evt.key === 'Home') {
          evt.preventDefault();
          scroller.scrollTo({ left: 0, behavior: 'smooth' });
        } else if (evt.key === 'End') {
          evt.preventDefault();
          scroller.scrollTo({ left: scroller.scrollWidth, behavior: 'smooth' });
        }
      });

      var scrollByCard = function (direction, evt) {
        if (evt) {
          evt.preventDefault();
          evt.stopPropagation();
        }
        var firstCard = scroller.querySelector('.cart-upsells__card');
        var step = firstCard && firstCard.offsetWidth > 0
          ? firstCard.offsetWidth + 10
          : Math.max(200, scroller.clientWidth * 0.8);
        var max = scroller.scrollWidth - scroller.clientWidth;
        var target = Math.max(0, Math.min(max, scroller.scrollLeft + direction * step));
        try {
          scroller.scrollTo({ left: target, behavior: 'smooth' });
        } catch (e) {
          scroller.scrollLeft = target;
        }
      };
      // Use pointerdown so the arrow responds even if a parent click handler
      // tries to consume the click. preventDefault stops the implicit focus
      // shift so the bar's hover/visibility state stays put mid-click.
      var bindArrow = function (arrow, dir) {
        if (!arrow) return;
        var fire = function (evt) {
          if (arrow.classList.contains('is-disabled')) return;
          scrollByCard(dir, evt);
        };
        arrow.addEventListener('click', fire);
        arrow.addEventListener('pointerdown', function (evt) {
          // Don't let the wheel/drag handlers see this as a card interaction.
          evt.stopPropagation();
        });
      };
      bindArrow(prevArrow, -1);
      bindArrow(nextArrow, 1);
      scroller.addEventListener('scroll', updateThumb, { passive: true });
      window.addEventListener('resize', updateThumb);
      updateThumb();
      setTimeout(updateThumb, 200);

      // Safety net: poll every 100ms in case scroll events misfire.
      this._scrollPoll = setInterval(function () {
        if (!document.contains(scroller)) {
          clearInterval(self._scrollPoll);
          self._scrollPoll = null;
          return;
        }
        updateThumb();
      }, 100);

      // Vertical wheel → horizontal scroll.
      scroller.addEventListener('wheel', function (evt) {
        var dy = evt.deltaY;
        if (Math.abs(dy) <= Math.abs(evt.deltaX)) return;
        var max = scroller.scrollWidth - scroller.clientWidth;
        if (max <= 0) return;
        var atLeftEdge = scroller.scrollLeft <= 0 && dy < 0;
        var atRightEdge = scroller.scrollLeft >= max && dy > 0;
        if (atLeftEdge || atRightEdge) return;
        evt.preventDefault();
        scroller.scrollLeft += dy;
      }, { passive: false });

      // Click-and-drag horizontal scrolling.
      var dragging = false;
      var dragStartX = 0;
      var dragStartScroll = 0;
      var moved = false;
      scroller.addEventListener('mousedown', function (evt) {
        if (evt.target.closest('button, select, input')) return;
        dragging = true;
        moved = false;
        dragStartX = evt.pageX;
        dragStartScroll = scroller.scrollLeft;
        scroller.classList.add('is-dragging');
      });
      window.addEventListener('mousemove', function (evt) {
        if (!dragging) return;
        var dx = evt.pageX - dragStartX;
        if (Math.abs(dx) > 3) moved = true;
        scroller.scrollLeft = dragStartScroll - dx;
      });
      window.addEventListener('mouseup', function () {
        if (!dragging) return;
        dragging = false;
        scroller.classList.remove('is-dragging');
      });
      scroller.addEventListener('click', function (evt) {
        if (moved) {
          evt.preventDefault();
          evt.stopPropagation();
          moved = false;
        }
      }, true);
    }


    _renderCard(p) {
      var firstAvailable =
        (p.variants && p.variants.find(function (v) { return v.available !== false; })) ||
        (p.variants && p.variants[0]) || null;
      var hasMultipleVariants = p.variants && p.variants.length > 1;

      // Variant label — shown only when there's no dropdown to pick one
      // (single-variant products with a non-default title).
      var variantLabel = '';
      if (
        !hasMultipleVariants &&
        firstAvailable &&
        firstAvailable.title &&
        firstAvailable.title !== 'Default Title'
      ) {
        variantLabel =
          '<span class="cart-upsells__card-variant" data-variant-label>' +
            escapeHtml(firstAvailable.title) +
          '</span>';
      }

      // Multi-variant cards show "From $X" using the cheapest variant; the
      // change handler swaps to the picked variant's price once the user picks.
      var displayPrice = p.price;
      var fromPrefix = '';
      if (hasMultipleVariants) {
        var prices = p.variants
          .map(function (v) { return parseFloat(v.price); })
          .filter(function (n) { return isFinite(n); });
        if (prices.length) displayPrice = Math.min.apply(null, prices);
        fromPrefix = '<span class="cart-upsells__card-price-prefix" data-price-prefix>From </span>';
      }

      var priceHtml = '';
      if (p.comparePrice && Number(p.comparePrice) > Number(displayPrice)) {
        priceHtml =
          fromPrefix +
          '<span class="cart-upsells__card-price">' + formatPrice(displayPrice) + '</span>' +
          '<span class="cart-upsells__card-compare">' + formatPrice(p.comparePrice) + '</span>';
      } else if (displayPrice != null) {
        priceHtml =
          fromPrefix +
          '<span class="cart-upsells__card-price">' + formatPrice(displayPrice) + '</span>';
      }
      priceHtml = variantLabel + priceHtml;

      var ctaHtml = '';
      if (p.variants && p.variants.length > 1) {
        // Multi-variant — single-step: button shows "Choose [option]" and
        // expands a <select> + confirm Add button below.
        var firstAvailable = p.variants.find(function (v) { return v.available !== false; }) || p.variants[0];
        var optionsHtml = p.variants.map(function (v) {
          var disabled = v.available === false ? ' disabled' : '';
          var selected = v.id === firstAvailable.id ? ' selected' : '';
          var label = v.title || (Array.isArray(v.options) ? v.options.join(' / ') : '');
          return '<option value="' + escapeAttr(v.id) +
            '" data-price="' + escapeAttr(v.price) + '"' + selected + disabled + '>' +
            escapeHtml(label) + ' — ' + formatPrice(v.price) +
            '</option>';
        }).join('');
        var optionAriaLabel = 'Choose ' + (p.optionName || 'option') + ' for ' + p.title;
        ctaHtml =
          '<button type="button" class="cart-upsells__card-cta" data-toggle-options' +
            ' aria-expanded="false" aria-controls="upsell-options-' + escapeAttr(p.id || '') + '">' +
            'Choose ' + escapeHtml(p.optionName || 'option') +
          '</button>' +
          '<div class="cart-upsells__card-options"' +
            ' id="upsell-options-' + escapeAttr(p.id || '') + '"' +
            ' aria-hidden="true">' +
            '<select class="cart-upsells__card-select" data-variant-select' +
              ' aria-label="' + escapeAttr(optionAriaLabel) + '">' + optionsHtml + '</select>' +
            '<button type="button" class="cart-upsells__card-cta cart-upsells__card-cta--confirm"' +
              ' data-variant-id="' + escapeAttr(firstAvailable.id) + '"' +
              ' data-product-title="' + escapeAttr(p.title) + '"' +
              ' aria-label="Add ' + escapeAttr(p.title) + ' to cart">' +
              'Add' +
            '</button>' +
          '</div>';
      } else if (p.variantId) {
        ctaHtml = '<button type="button" class="cart-upsells__card-cta"' +
          ' data-variant-id="' + escapeAttr(p.variantId) + '"' +
          ' data-product-title="' + escapeAttr(p.title) + '">Add</button>';
      }

      return (
        '<article class="cart-upsells__card" data-product-id="' + escapeAttr(p.id || '') + '">' +
          '<a href="' + escapeAttr(p.url) + '" class="cart-upsells__card-link" tabindex="-1">' +
            (p.image
              ? '<img class="cart-upsells__card-image" src="' + escapeAttr(shopifyImageAt(p.image, 300)) +
                '" alt="' + escapeAttr(p.title) + '" loading="lazy" decoding="async">'
              : '<span class="cart-upsells__card-image cart-upsells__card-image--empty"></span>') +
          '</a>' +
          '<div class="cart-upsells__card-body">' +
            '<h4 class="cart-upsells__card-title">' +
              '<a href="' + escapeAttr(p.url) + '">' + escapeHtml(p.title) + '</a>' +
            '</h4>' +
            '<div class="cart-upsells__card-pricing">' + priceHtml + '</div>' +
            ctaHtml +
          '</div>' +
        '</article>'
      );
    }
  }

  if (!customElements.get('cart-upsells')) {
    customElements.define('cart-upsells', CartUpsells);
  }

  // ---------------------------------------------------------------------------
  // Variant picker — clicking the "Choose [option]" CTA reveals a <select> +
  // confirm Add button. Selecting a variant updates the confirm button's price
  // label and data-variant-id.
  // ---------------------------------------------------------------------------
  document.addEventListener('click', function (evt) {
    var toggle = evt.target.closest('[data-toggle-options]');
    if (!toggle) return;
    evt.preventDefault();
    evt.stopPropagation();
    var card = toggle.closest('.cart-upsells__card');
    if (!card) return;
    var panel = card.querySelector('.cart-upsells__card-options');
    if (!panel) return;
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    toggle.setAttribute('aria-expanded', 'true');
    toggle.hidden = true;
    var sel = panel.querySelector('[data-variant-select]');
    if (sel) sel.focus();
  });

  // Capture phase + stopPropagation: the variant <select> lives inside the
  // cart-drawer's form, and <cart-drawer-items> runs quantity validation on any
  // change event in that subtree. We stop the event before it ever bubbles up
  // so cart.js never tries to read step/min/max off our select.
  document.addEventListener('change', function (evt) {
    var sel = evt.target.closest('[data-variant-select]');
    if (!sel) return;
    evt.stopPropagation();
    sel.setCustomValidity('');
    var card = sel.closest('.cart-upsells__card');
    if (!card) return;
    var confirm = card.querySelector('.cart-upsells__card-cta--confirm');
    var opt = sel.options[sel.selectedIndex];
    if (!opt) return;
    if (confirm) {
      confirm.dataset.variantId = sel.value;
      confirm.textContent = 'Add';
    }
    // Keep the variant label (next to the price) in sync.
    var label = card.querySelector('[data-variant-label]');
    if (label) {
      // Strip the trailing "— $X" from the option text to get just the variant title.
      var optText = (opt.textContent || '').split('—')[0].trim();
      if (optText) label.textContent = optText;
    }
    // Keep the headline price in sync with the chosen variant; drop the
    // "From " prefix once a real choice has been made.
    var priceEl = card.querySelector('.cart-upsells__card-price');
    if (priceEl && opt.dataset.price != null) {
      priceEl.textContent = formatPrice(opt.dataset.price);
    }
    var prefix = card.querySelector('[data-price-prefix]');
    if (prefix) prefix.remove();
  }, true);

  // Same treatment for the bare select input/click events that cart.js's
  // quantity validators sometimes also listen for.
  document.addEventListener('input', function (evt) {
    if (evt.target.closest('[data-variant-select]')) evt.stopPropagation();
  }, true);

  // ---------------------------------------------------------------------------
  // Add-to-cart handler — delegated, refreshes the drawer via Section Rendering.
  // ---------------------------------------------------------------------------
  document.addEventListener('click', async function (evt) {
    var btn = evt.target.closest('.cart-upsells__card-cta[data-variant-id]');
    if (!btn) return;
    evt.preventDefault();
    evt.stopPropagation();

    var variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.disabled = true;
    btn.classList.add('is-loading');

    try {
      var formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      // Ask for the cart-drawer + cart-icon-bubble HTML back so we can swap in.
      formData.append('sections', 'cart-drawer,cart-icon-bubble');
      formData.append('sections_url', window.location.pathname);

      var r = await fetch('/cart/add.js', { method: 'POST', body: formData });
      if (!r.ok) throw new Error('add failed: ' + r.status);
      var data = await r.json();

      // Render returned sections.
      if (data.sections && typeof data.sections === 'object') {
        Object.keys(data.sections).forEach(function (sectionId) {
          var html = data.sections[sectionId];
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          if (sectionId === 'cart-drawer') {
            var fresh = doc.querySelector('.cart-drawer-listo__inner');
            var live = document.querySelector('cart-drawer .cart-drawer-listo__inner');
            if (fresh && live) live.innerHTML = fresh.innerHTML;
          } else if (sectionId === 'cart-icon-bubble') {
            var freshBubble = doc.querySelector('#cart-icon-bubble');
            var liveBubble = document.querySelector('#cart-icon-bubble');
            if (freshBubble && liveBubble) liveBubble.innerHTML = freshBubble.innerHTML;
          }
        });
      }

      // Notify other listeners (cart count, route widget, our own subscription).
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsells', variantId: variantId });
      }

      // Open the drawer if it's not already.
      var drawer = document.querySelector('cart-drawer');
      if (drawer && !drawer.classList.contains('active') && typeof drawer.open === 'function') {
        drawer.open();
      }
    } catch (err) {
      console.warn('[cart-upsells] add to cart failed', err);
    } finally {
      btn.disabled = false;
      btn.classList.remove('is-loading');
    }
  });
})();
